/**
 * services/InvitationTokenService.ts
 * Invitation token business logic and orchestration
 *
 * Architecture: GPT #8 - Service stays orchestration-only
 *
 * Handles invitation token lifecycle for beta application approval flow.
 * Tokens link to beta_applications (not users, since users don't exist yet).
 * When user activates account, auth.users is created (trigger creates public.users),
 * then token is marked as used and linked to the new user.
 *
 * All data access delegated to invitationTokensRepository.
 */

import type { BetaApplication } from '@/types/supabase/BetaApplication';
import type {
  InvitationToken
} from '@/types/supabase/InvitationToken';
import type {
  InvitationTokenValidation,
  InvitationTokenWithUser
} from '@/types/invitation';
import { invitationTokensRepository } from '@/repositories/invitationTokensRepository';

/**
 * Invitation Token Service
 *
 * Thin wrapper over invitationTokensRepository providing business logic.
 * No direct database access - all queries through repository.
 *
 * GPT Recommendations Applied:
 * - #1: Token secrets never leaked (repository uses TOKEN_SAFE by default)
 * - #2: One active invite per application (repository enforces)
 * - #5: Time consistency (UTC helpers, server-side now())
 * - #8: Orchestration-only (no query shaping)
 */
export class InvitationTokenService {

  // ===== TOKEN GENERATION METHODS =====

  /**
   * Create invitation token from approved beta application
   *
   * Repository handles:
   * - Invalidating existing active tokens (GPT #2)
   * - Security projection (GPT #1 - no token in return)
   *
   * @param application - Beta application that was approved
   * @param expiresInHours - Hours until token expires (default: 72)
   * @returns Promise resolving to created invitation token (without token field)
   */
  async createFromBetaApplication(
    application: BetaApplication,
    expiresInHours: number = 72
  ): Promise<InvitationToken> {
    try {
      // Check if active invitation token already exists
      const existingToken = await invitationTokensRepository.getActiveToken(application.id);
      if (existingToken) {
        console.log(`⚠️ Active invitation token already exists for beta application ${application.id}`);
        return existingToken;
      }

      // Create new token (repository will invalidate any existing active tokens)
      const newToken = await invitationTokensRepository.create({
        betaApplicationId: application.id,
        token: this.generateSecureToken(32),
        expiresAt: this.calculateExpirationDate(expiresInHours),
        usedAt: null
      });

      console.log(`✅ Created invitation token for ${application.email} (expires: ${newToken.expiresAt})`);

      return newToken;
    } catch (error) {
      console.error(`❌ Failed to create invitation token for application ${application.id}:`, error);
      throw new Error('Failed to create invitation token');
    }
  }

  // ===== TOKEN VALIDATION METHODS =====

  /**
   * Validate invitation token and return beta application information
   *
   * Uses RPC validation which returns tokens with validity info,
   * allowing distinction between expired, already_used, not_approved, and not_found.
   *
   * @param token - Token string to validate
   * @returns Promise resolving to validation result
   */
  async validateToken(token: string): Promise<InvitationTokenValidation> {
    try {
      // Use repository for data access (includes validity info from RPC)
      const result = await invitationTokensRepository.getByTokenWithApplication(token);

      // Token not found in database
      if (!result) {
        return {
          isValid: false,
          error: 'not_found'
        };
      }

      // Build validation result with camelCase token and application
      const tokenWithApp: InvitationTokenWithUser = {
        ...result.token,
        betaApplications: result.application
      };

      // Use validity info from RPC (already computed server-side)
      if (!result.isValid) {
        return {
          isValid: false,
          error: result.invalidReason ?? 'not_found',
          token: tokenWithApp
        };
      }

      // Token is valid
      console.log(`Invitation token validated for ${result.application.email}`);
      return {
        isValid: true,
        token: tokenWithApp
      };
    } catch (error) {
      console.error('Failed to validate invitation token:', error);
      throw new Error('Failed to validate invitation token');
    }
  }

  /**
   * Mark token as used and link to created user
   *
   * Called after user account is successfully created during activation.
   * Idempotent - safe to call multiple times.
   *
   * @param tokenId - ID of token to mark as used
   * @param userId - ID of user that was created
   * @returns Promise resolving to true if updated, false if already used
   */
  async markAsUsed(tokenId: string, userId: string): Promise<boolean> {
    try {
      const wasUpdated = await invitationTokensRepository.markAsUsed(tokenId, userId);

      if (wasUpdated) {
        console.log(`✅ Invitation token ${tokenId} marked as used by user ${userId}`);
      } else {
        console.log(`⚠️ Invitation token ${tokenId} already used or not found`);
      }

      return wasUpdated;
    } catch (error) {
      console.error('Failed to mark invitation token as used:', error);
      throw new Error('Failed to mark invitation token as used');
    }
  }

  /**
   * Extend expiration date of an existing token
   *
   * Used when sending activation reminders to give users more time.
   *
   * @param tokenId - ID of token to extend
   * @param expiresInHours - Hours from now for new expiration (default: 72)
   * @returns Promise resolving to updated token, or null if already used/expired
   */
  async extendExpiration(tokenId: string, expiresInHours: number = 72): Promise<InvitationToken | null> {
    try {
      const newExpiresAt = this.calculateExpirationDate(expiresInHours);

      // Repository guards against extending used/expired tokens (GPT #5)
      const updatedToken = await invitationTokensRepository.extendExpiration(tokenId, newExpiresAt);

      if (!updatedToken) {
        console.log(`⚠️ Cannot extend invitation token ${tokenId} - already used or expired`);
        return null;
      }

      console.log(`✅ Extended invitation token ${tokenId} expiration to ${updatedToken.expiresAt}`);
      return updatedToken;
    } catch (error) {
      console.error('Failed to extend invitation token expiration:', error);
      throw new Error('Failed to extend invitation token expiration');
    }
  }

  // ===== TOKEN QUERY METHODS =====

  /**
   * Get active (unused, non-expired) token for beta application
   *
   * @param betaApplicationId - Beta application ID
   * @returns Promise resolving to active token or null
   */
  async getActiveTokenForApplication(betaApplicationId: string): Promise<InvitationToken | null> {
    try {
      return await invitationTokensRepository.getActiveToken(betaApplicationId);
    } catch (error) {
      console.error(`Failed to get active token for application ${betaApplicationId}:`, error);
      throw new Error('Failed to retrieve active invitation token');
    }
  }

  /**
   * Get invitation token by token string
   *
   * @param token - Token string
   * @returns Promise resolving to token or null
   */
  async getByToken(token: string): Promise<InvitationToken | null> {
    try {
      return await invitationTokensRepository.getByToken(token);
    } catch (error) {
      console.error('Failed to get invitation token by token string:', error);
      throw new Error('Failed to retrieve invitation token');
    }
  }

  // ===== CLEANUP METHODS =====

  /**
   * Clean up expired tokens
   *
   * @param olderThanDays - Remove tokens older than this many days (default: 30)
   * @returns Promise resolving to number of tokens cleaned
   */
  async cleanExpiredTokens(olderThanDays: number = 30): Promise<number> {
    try {
      const cleanedCount = await invitationTokensRepository.cleanExpired(olderThanDays);

      console.log(`🧹 Cleaned ${cleanedCount} expired invitation tokens`);
      return cleanedCount;
    } catch (error) {
      console.error('Failed to clean expired invitation tokens:', error);
      throw new Error('Failed to clean expired invitation tokens');
    }
  }

  // ===== PRIVATE UTILITY METHODS =====
  // GPT #5: Time consistency - UTC helpers kept in service

  /**
   * Generate cryptographically secure token
   *
   * @param length - Length of token to generate
   * @returns Secure random token string
   */
  private generateSecureToken(length: number = 32): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';

    // Use crypto.getRandomValues for cryptographically secure randomness
    const randomArray = new Uint8Array(length);

    if (typeof crypto === 'undefined' || !crypto.getRandomValues) {
      throw new Error('Crypto API not available for secure token generation');
    }

    crypto.getRandomValues(randomArray);

    for (let i = 0; i < length; i++) {
      const randomValue = randomArray[i];
      if (randomValue !== undefined) {
        result += chars.charAt(randomValue % chars.length);
      }
    }

    return result;
  }

  /**
   * Calculate expiration date from hours
   *
   * GPT #5: Time consistency - standardized UTC calculation
   *
   * @param hours - Hours from now for expiration
   * @returns ISO string of expiration date
   */
  private calculateExpirationDate(hours: number): string {
    const expirationDate = new Date();
    expirationDate.setTime(expirationDate.getTime() + (hours * 60 * 60 * 1000));
    return expirationDate.toISOString();
  }
}

// Export singleton instance
export const invitationTokenService = new InvitationTokenService();
